import { NavLink } from 'react-router-dom'
import { ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'
import { APP_NAME } from '@/lib/brand'
import { usePermissions } from '@/hooks/usePermissions'
import { SIDEBAR_GROUPS, type SidebarGroup, type SidebarItem } from './sidebar-config'
import { useCollapsedGroups, type CollapsedGroupsApi } from './useCollapsedGroups'

interface SidebarContentProps {
  /** Callback disparado ao clicar em qualquer item do menu. Usado pelo
   *  `<MobileSidebarDrawer>` pra se fechar quando o usuário navega. */
  readonly onNavigate?: () => void
}

/**
 * Conteúdo interno da sidebar (marca + grupos + itens). Usado tanto pelo
 * `<Sidebar>` fixo do desktop quanto pelo `<MobileSidebarDrawer>` no mobile.
 * Em ambos os casos o container externo já garante largura adequada (`w-64`).
 */
export function SidebarContent({ onNavigate }: SidebarContentProps) {
  const { hasAny } = usePermissions()
  const collapseApi = useCollapsedGroups()

  const visibleGroups = SIDEBAR_GROUPS.map((group) => ({
    ...group,
    items: group.items.filter((item) => hasAny(...item.permissions)),
  })).filter((group) => group.items.length > 0)

  return (
    <>
      <div className="flex h-16 shrink-0 items-center border-b border-border px-4">
        <span className="text-base font-semibold text-foreground">{APP_NAME}</span>
      </div>
      <nav className="flex-1 overflow-y-auto py-3">
        {visibleGroups.map((group, idx) => (
          <SidebarGroupSection
            key={group.id}
            group={group}
            isFirst={idx === 0}
            collapseApi={collapseApi}
            onNavigate={onNavigate}
          />
        ))}
      </nav>
    </>
  )
}

interface SidebarGroupSectionProps {
  readonly group: SidebarGroup
  readonly isFirst: boolean
  readonly collapseApi: CollapsedGroupsApi
  readonly onNavigate?: () => void
}

function SidebarGroupSection({ group, isFirst, collapseApi, onNavigate }: SidebarGroupSectionProps) {
  const collapsed = collapseApi.isCollapsed(group.id)
  return (
    <div className={cn(!isFirst && 'mt-2')}>
      <button
        type="button"
        onClick={() => collapseApi.toggle(group.id)}
        aria-expanded={!collapsed}
        aria-controls={`sidebar-group-${group.id}`}
        className={cn(
          'flex w-full items-center justify-between rounded-md px-3 py-1 transition-colors',
          'text-[10px] font-semibold uppercase tracking-wider text-muted-foreground',
          'hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        )}
      >
        <span>{group.label}</span>
        <ChevronDown
          size={12}
          className={cn(
            'shrink-0 transition-transform duration-200',
            collapsed && '-rotate-90',
          )}
          aria-hidden
        />
      </button>
      {/* Animação de altura via grid-template-rows (0fr -> 1fr): transição CSS-only. */}
      <div
        id={`sidebar-group-${group.id}`}
        className={cn(
          'grid transition-[grid-template-rows] duration-200 ease-out',
          collapsed ? 'grid-rows-[0fr]' : 'grid-rows-[1fr]',
        )}
      >
        <div className="overflow-hidden">
          <ul className="space-y-0.5 px-2 pt-1">
            {group.items.map((item) => (
              <li key={item.href}>
                <SidebarItemLink item={item} onNavigate={onNavigate} />
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  )
}

interface SidebarItemLinkProps {
  readonly item: SidebarItem
  readonly onNavigate?: () => void
}

function SidebarItemLink({ item, onNavigate }: SidebarItemLinkProps) {
  const Icon = item.icon
  return (
    <NavLink
      to={item.href}
      onClick={onNavigate}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-3 rounded-md px-2 py-2 text-sm font-medium transition-colors',
          isActive
            ? 'bg-primary/10 text-primary'
            : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
        )
      }
      title={item.label}
    >
      <Icon size={18} className="shrink-0" />
      <span>{item.label}</span>
    </NavLink>
  )
}
